<template>
  <div
    v-on-click-outside="closeAndClear"
    class="w-92 pt-2"
    @blur="closeAndClear"
  >
    <lf-input
      :value="searchTerm"
      :placeholder="placeholder"
      name="attributeSearch"
      :custom-error="error"
      no-margin
      no-autocomplete
      @click.stop="open"
      @focusin="open"
      @focusout="error = ''"
      @key-released="search"
      @keydown.enter="selectHighlighted"
      @keydown.esc="closeAndClear"
      @keydown.prevent.up="highlightPrevious"
      @keydown.prevent.down="highlightNext"
    >
      <template #suffix>
        <icon-base
          :icon="IconEnter"
          class="cursor-pointer rounded-sm text-disabled-medium"
          width="20"
          height="20"
        />
      </template>
    </lf-input>

    <formula-dropdown
      v-show="isOpen && filteredItems.length"
      ref="optionsListRef"
      class="absolute z-1 max-h-72 w-92 overflow-y-auto rounded-sm bg-white p-2 shadow-lg"
      :options="filteredItems"
      :needle="searchTerm"
      :highlighted-index="highlightedIndex"
      :found-options-count="foundItemsCount"
      @select="select"
    >
      <template #append>
        <div
          v-show="foundItemsCount > filteredItems.length"
          class="rounded-sm p-2 text-sm text-disabled-medium"
        >
          {{ moreItemsCountText }}
        </div>
      </template>
    </formula-dropdown>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, ref, unref } from "vue";
import { useI18n } from "vue-i18n";

import debounce from "lodash/debounce";

import { MathOperation } from "@/enums/customAttributes";
import { useAttributeBuilderServices } from "@/hooks/attributeBuilder";
import { useSearchSelectProvider } from "@/hooks/searchSelectProvider";

import type { CustomAttributeFormulaItem } from "@/models/customAttributes/customAttribute";
import type {
  CustomAttributeDropdownOption,
  FilterFunction,
  UseSearchSelectProviderReturn,
} from "@/models/customAttributes/searchSelectDropdown";

const FormulaDropdown = defineAsyncComponent(
  () => import("@/components/customAttributes/formula/FormulaDropdown.vue")
);
const IconEnter = defineAsyncComponent(
  () => import("@/components/icons/IconEnter.vue")
);

const emit = defineEmits<{
  (
    eventName: "select",
    item: CustomAttributeFormulaItem | CustomAttributeFormulaItem[]
  ): void;
  (eventName: "close"): void;
}>();

const { t } = useI18n();
const { serviceAttributes, getServiceProviderDetails } =
  useAttributeBuilderServices();

const placeholder = t("ATTRIBUTE_BUILDER.FORMULA_PLACEHOLDER");

const filterFunction: FilterFunction<CustomAttributeDropdownOption> = (
  needle,
  items
) =>
  items.filter(
    ({ label, name }) =>
      label?.toLowerCase().includes(needle.toLowerCase()) ||
      name.toLowerCase().includes(needle.toLowerCase())
  );

const error = ref("");
const searchTerm = ref("");

const optionsListRef = ref<{
  optionsListRef: HTMLElement | null;
} | null>(null);

const formulaDropdownRef = computed(() =>
  unref(optionsListRef.value?.optionsListRef ?? null)
);

const serviceAttributesOptions = computed(() => {
  return serviceAttributes.value.map((attr) => {
    const provider = getServiceProviderDetails(attr.id);
    return {
      name: attr.name,
      label: [provider?.title, provider?.name, provider?.category]
        .filter(Boolean)
        .join(" / "),
      id: attr.id,
      ...((attr.options?.month_picker_range ||
        attr.options?.consumer_score_models_levels) && {
        context: undefined,
      }),
      ...(attr.context && { context: attr.context }),
    };
  });
});

const servicesWithOperations = computed(() => {
  return [
    ...Object.keys(MathOperation).map((operation) => ({
      name: operation,
      label: `${operation} (${
        MathOperation[operation as keyof typeof MathOperation]
      })`,
      operation: true,
    })),
    { name: "parenthesis", label: "parenthesis", formula: true },
    ...serviceAttributesOptions.value,
  ];
});

const extendedOptions = computed<CustomAttributeDropdownOption[]>(() =>
  !!searchTerm.value.trim() && !isNaN(Number(searchTerm.value))
    ? [
        {
          name: searchTerm.value,
          search: true,
        },
        ...servicesWithOperations.value,
      ]
    : servicesWithOperations.value
);

const moreItemsCountText = computed(
  () =>
    `${t("COMMON.AND")} ${
      foundItemsCount.value - filteredItems.value.length
    } ${t("COMMON.MORE").toLowerCase()}`
);

const {
  isOpen,
  filteredItems,
  foundItemsCount,
  highlightedIndex,
  open,
  closeAndClear,
  select,
  selectHighlighted,
  highlightPrevious,
  highlightNext,
  onSelected,
  onClose,
}: UseSearchSelectProviderReturn = useSearchSelectProvider(
  {
    needle: searchTerm,
    items: extendedOptions,
    filterFunction,
  },
  {
    initialHighlightedIndex: 0,
    scrollRef: formulaDropdownRef,
    slice: 20,
  }
);

const search: (value: string) => void = debounce((value: string) => {
  error.value = "";
  open();
  searchTerm.value = value;
}, 300);

onClose(() => {
  emit("close");
});

onSelected((value: (typeof extendedOptions.value)[number] | undefined) => {
  if (!value) {
    error.value = placeholder;
    return;
  }
  error.value = "";
  let formulaItem: CustomAttributeFormulaItem | CustomAttributeFormulaItem[];

  if (value.formula) {
    formulaItem = [];
  } else if (value.operation) {
    formulaItem = MathOperation[value.name as keyof typeof MathOperation];
  } else if (value.search) {
    formulaItem = Number(value.name);
  } else if (value.id) {
    formulaItem = {
      id: value.id,
      ...(Object.hasOwnProperty.call(value, "context") && {
        context: value.context,
      }),
    };
  } else {
    return;
  }
  emit("select", formulaItem);
  closeAndClear();
});
</script>
